/**
 * The pieces the pink permit sheet is drawn from (ADR 0289): a labelled line,
 * a tick box, and the editor that turns a filled box into a box to type in
 * while the office is checking the form.
 */

import {
  formatMoney,
  parsePermitAmount,
  permitAmountText,
  permitDateText,
  type PermitApplication,
} from "@roof-hawk/core";
import { useEffect, useState, type ReactNode } from "react";

import { t } from "../../../i18n";

/** A label and the line it is written on. */
export function Line({
  label,
  children,
}: {
  label: string;
  children?: ReactNode;
}) {
  return (
    <div className="kdoc-line">
      {label ? <span className="kdoc-line-label">{label}</span> : null}
      <span className="kdoc-line-fill">{children}</span>
    </div>
  );
}

/** A tick box, ticked where the office ticks it. */
export function Box({ checked = false }: { checked?: boolean }) {
  return (
    <span
      className={`kdoc-box${checked ? " on" : ""}`}
      role="img"
      aria-label={checked ? t("ticked") : t("not ticked")}
    >
      {checked ? "✓" : ""}
    </span>
  );
}

/**
 * The amount, typed. Held as words while somebody types and turned into cents
 * when they leave the box, so "24,533.3" mid-keystroke is not rejected.
 */
function AmountInput({
  value,
  onChange,
}: {
  value: PermitApplication["contractAmount"];
  onChange: (amount: PermitApplication["contractAmount"]) => void;
}) {
  const [typed, setTyped] = useState(() => permitAmountText(value));
  useEffect(() => setTyped(permitAmountText(value)), [value]);
  const parsed = parsePermitAmount(typed);
  return (
    <input
      className={`kdoc-in kdoc-grow${parsed === null ? " kdoc-bad" : ""}`}
      aria-label={t("Total contract amount")}
      inputMode="decimal"
      value={typed}
      title={
        parsed === null
          ? t("Type an amount like 24,533.32")
          : formatMoney(parsed)
      }
      onChange={(e) => setTyped(e.target.value)}
      onBlur={() => {
        if (parsed !== null) onChange(parsed);
        else setTyped(permitAmountText(value));
      }}
    />
  );
}

/** How a sheet draws a box the office fills, and writes back what they type. */
export interface PermitEditor {
  editing: boolean;
  d: PermitApplication;
  text: (
    value: string,
    change: (value: string) => void,
    label: string,
    className?: string,
  ) => ReactNode;
  date: (
    value: string,
    change: (value: string) => void,
    label: string,
  ) => ReactNode;
  amount: () => ReactNode;
  set: (patch: Partial<PermitApplication>) => void;
  setOwner: (patch: Partial<PermitApplication["owner"]>) => void;
  setSite: (patch: Partial<PermitApplication["site"]>) => void;
  setContractor: (patch: Partial<PermitApplication["contractor"]>) => void;
}

export function permitEditor(
  d: PermitApplication,
  onChange: ((next: PermitApplication) => void) | null,
): PermitEditor {
  const editing = onChange !== null;
  const set = (patch: Partial<PermitApplication>) =>
    onChange?.({ ...d, ...patch });
  return {
    editing,
    d,
    set,
    setOwner: (patch) => set({ owner: { ...d.owner, ...patch } }),
    setSite: (patch) => set({ site: { ...d.site, ...patch } }),
    setContractor: (patch) =>
      set({ contractor: { ...d.contractor, ...patch } }),
    text: (value, change, label, className = "") =>
      editing ? (
        <input
          className={`kdoc-in ${className}`}
          aria-label={label}
          value={value}
          onChange={(e) => change(e.target.value)}
        />
      ) : (
        <span className={`kdoc-val ${className}`}>{value}</span>
      ),
    date: (value, change, label) =>
      editing ? (
        <input
          type="date"
          className="kdoc-in kdoc-in-date"
          aria-label={label}
          value={value}
          onChange={(e) => change(e.target.value)}
        />
      ) : (
        <span className="kdoc-val">{permitDateText(value)}</span>
      ),
    amount: () =>
      editing ? (
        <AmountInput
          value={d.contractAmount}
          onChange={(amount) => set({ contractAmount: amount })}
        />
      ) : (
        <span className="kdoc-val">{permitAmountText(d.contractAmount)}</span>
      ),
  };
}
